import React, { useState, useEffect, useCallback } from "react";
import {
  SERVER_SORTABLE_FIELDS,
  SERVER_FILTERABLE_FIELDS,
  workflowStatuses,
} from "../utils/constants";
import {
  Tooltip,
  IconButton,
  Popover,
  TextField,
  Button,
  makeStyles,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Typography,
} from "@material-ui/core";

import FilterListIcon from "@material-ui/icons/FilterList";
import ArrowDownwardIcon from "@material-ui/icons/ArrowDownward";
import ArrowUpwardIcon from "@material-ui/icons/ArrowUpward";
import SortIcon from "@material-ui/icons/Sort";
import SearchIcon from "@material-ui/icons/Search";
import WorkflowNameInput from "./WorkflowNameInput";
import Dropdown from "./Dropdown";
import CustomSortIcon from "./CustomSortIcon";

const useStyles = makeStyles((theme) => ({
  filterPopover: {
    padding: theme.spacing(2),
    minWidth: "250px",
  },
  filterActions: {
    display: "flex",
    justifyContent: "flex-end",
    marginTop: theme.spacing(2),
    gap: theme.spacing(1),
  },
  filterIcon: {
    color: "#5f6368",
    padding: 4,
    "&.active": {
      color: theme.palette.primary.main,
    },
    "&:hover": {
      backgroundColor: "rgba(0, 0, 0, 0.04)",
    },
  },
  headerContainer: {
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    fontFamily: "Poppins, sans-serif",
  },
  headerContent: {
    display: "flex",
    alignItems: "center",
    minWidth: 0,
    overflow: "hidden",
    flex: "1 1 auto",
    padding: "0 4px",
    fontFamily: "Poppins, sans-serif",
  },
  headerTextContainer: {
    display: "flex",
    alignItems: "center",
    flexShrink: 1,
    overflow: "hidden",
    fontFamily: "Poppins, sans-serif",
  },
  headerText: {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    padding: "0 6px",
    fontWeight: "Medium",
    fontSize: "13px",
    color: "#202124",
    letterSpacing: "0.01px",
    textTransform: "uppercase",
    fontFamily: "'poppins', sans-serif",
  },
  iconsContainer: {
    display: "flex",
    alignItems: "center",
    flexShrink: 0,
    gap: 2,
    marginLeft: 4,
  },
  sortButton: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    borderRadius: "50%",
    padding: 4,
    width: 28,
    height: 28,
    color: "#5f6368",
    "&:hover": {
      backgroundColor: "rgba(0, 0, 0, 0.04)",
    },
    "&.active": {
      color: theme.palette.primary.main,
    },
  },
  filterField: {
    marginBottom: theme.spacing(2),
    fontFamily: "'poppins', sans-serif",
  },
}));

const CustomHeaderComponent = React.memo((props) => {
  const {
    displayName,
    column,
    onSortChanged,
    currentSort,
    onFilterChanged,
    currentFilters = {},
    isWorkflowContext = false,
  } = props;

  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState(null);

  // For standard filters
  const [filterValue, setFilterValue] = useState(
    currentFilters[column.colId] || ""
  );

  // For workflow context filters
  const [workflowNameFilter, setWorkflowNameFilter] = useState(
    currentFilters["workflowName"] || []
  );
  const [categoryFilter, setCategoryFilter] = useState(
    currentFilters["category"] || ""
  );

  // Update filter values when currentFilters change
  useEffect(() => {
    if (filterValue !== (currentFilters[column.colId] || "")) {
      setFilterValue(currentFilters[column.colId] || "");
    }

    if (isWorkflowContext) {
      const newWorkflowFilter = currentFilters["workflowName"] || [];
      const newCategoryFilter = currentFilters["category"] || "";

      if (
        JSON.stringify(workflowNameFilter) !== JSON.stringify(newWorkflowFilter)
      ) {
        setWorkflowNameFilter(newWorkflowFilter);
      }

      if (categoryFilter !== newCategoryFilter) {
        setCategoryFilter(newCategoryFilter);
      }
    }
  }, [currentFilters, column.colId, isWorkflowContext]);

  const isServerSortable = SERVER_SORTABLE_FIELDS.includes(column.colId);
  const isServerFilterable = SERVER_FILTERABLE_FIELDS?.includes(column.colId);
  const hasActiveFilter = currentFilters && currentFilters[column.colId];

  const [sortField, sortDirection] = currentSort
    ? currentSort.split(":")
    : [null, null];

  const isCurrentSortField =
    isServerSortable &&
    (column.colId === sortField ||
      (column.colId === "workflowId" && sortField === "workflowId.keyword") ||
      (column.colId === "workflowType" &&
        sortField === "workflowType.keyword") ||
      (column.colId === "status" && sortField === "status.keyword") ||
      (column.colId === "reasonForIncompletion" &&
        sortField === "reasonForIncompletion.keyword"));

  const handleSortClick = (e) => {
    if (!isServerSortable) return;

    e.stopPropagation();
    let newSort;

    const fieldName = needsKeywordSuffix(column.colId)
      ? `${column.colId}.keyword`
      : column.colId;

    if (!isCurrentSortField) {
      newSort = `${fieldName}:DESC`;
    } else if (sortDirection === "DESC") {
      newSort = `${fieldName}:ASC`;
    } else {
      newSort = `${fieldName}:DESC`;
    }

    onSortChanged(newSort);
  };

  const needsKeywordSuffix = (field) => {
    const timeFields = ["startTime", "endTime", "updateTime"];
    if (timeFields.includes(field)) {
      return false;
    }
    return [
      "workflowId",
      "workflowType",
      "status",
      "correlationId",
      "requestId",
      "category",
      "event",
      "failedReferenceTaskNames",
      "reasonForIncompletion",
    ].includes(field);
  };

  // Filter related handlers
  const handleFilterClick = useCallback((event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  }, []);

  const handleFilterClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleFilterApply = useCallback(() => {
    if (onFilterChanged && !isWorkflowContext) {
      onFilterChanged(column.colId, filterValue);
    }
    handleFilterClose();
  }, [column.colId, filterValue, onFilterChanged, isWorkflowContext]);

  const handleWorkflowContextFilterApply = useCallback(() => {
    if (onFilterChanged && isWorkflowContext) {
      if (workflowNameFilter && workflowNameFilter.length > 0) {
        onFilterChanged("workflowName", workflowNameFilter);

        if (categoryFilter) {
          onFilterChanged("category", categoryFilter);
        }
      } else {
        onFilterChanged("workflowName", "");
        if (currentFilters["category"]) {
          onFilterChanged("category", "");
        }
      }
    }
    handleFilterClose();
  }, [
    workflowNameFilter,
    categoryFilter,
    onFilterChanged,
    isWorkflowContext,
    currentFilters,
  ]);

  const handleFilterClear = () => {
    setFilterValue("");
    if (onFilterChanged && !isWorkflowContext) {
      onFilterChanged(column.colId, "");
    }
    handleFilterClose();
  };

  const handleWorkflowContextFilterClear = () => {
    setWorkflowNameFilter([]);
    setCategoryFilter("");

    if (onFilterChanged && isWorkflowContext) {
      onFilterChanged("workflowName", "");
      onFilterChanged("category", "");
    }

    handleFilterClose();
  };

  const getFilterComponent = () => {
    if (isWorkflowContext && column.colId === "workflowName") {
      const isWorkflowNameEmpty =
        !workflowNameFilter || workflowNameFilter.length === 0;

      return (
        <>
          <div className={classes.filterField}>
            <Typography variant="subtitle2" gutterBottom>
              Filter by Workflow Reference Name
            </Typography>
            <WorkflowNameInput
              value={workflowNameFilter}
              onChange={(event, newValue) => {
                setWorkflowNameFilter(Array.isArray(newValue) ? newValue : []);
              }}
              placeholder="Select or type workflow Reference name"
              fullWidth
            />
          </div>

          <div className={classes.filterField}>
            <Typography
              variant="subtitle2"
              gutterBottom
              style={{ display: "flex", alignItems: "center" }}
            >
              Filter by Category
              <Typography
                variant="caption"
                color="textSecondary"
                style={{
                  marginLeft: 8,
                  backgroundColor: "#f1f3f4",
                  padding: "2px 6px",
                  borderRadius: "4px",
                  fontSize: "10px",
                  fontWeight: 500,
                }}
              >
                OPTIONAL
              </Typography>
              {isWorkflowNameEmpty && (
                <Typography
                  variant="caption"
                  color="textSecondary"
                  style={{ marginLeft: 8 }}
                >
                  (Select workflow name first)
                </Typography>
              )}
            </Typography>
            <FormControl fullWidth>
              <Select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                displayEmpty
                className={classes.selectEmpty}
                disabled={isWorkflowNameEmpty}
              >
                <MenuItem value="" disabled>
                  <em>Select Type</em>
                </MenuItem>
                <MenuItem value="Integration">Integration</MenuItem>
                <MenuItem value="Ticket">Ticket</MenuItem>
              </Select>
            </FormControl>
          </div>

          <div className={classes.filterActions}>
            <Button
              variant="outlined"
              color="default"
              onClick={handleWorkflowContextFilterClear}
            >
              Clear All
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleWorkflowContextFilterApply}
            >
              Apply
            </Button>
          </div>
        </>
      );
    }

    const timeFields = ["startTime", "endTime", "updateTime"];

    if (timeFields.includes(column.colId)) {
      return (
        <TextField
          label="Filter value"
          type="datetime-local"
          value={filterValue}
          onChange={(e) => setFilterValue(e.target.value)}
          InputLabelProps={{
            shrink: true,
          }}
          fullWidth
        />
      );
    } else if (column.colId === "status") {
      return (
        <div className={classes.filterField} style={{ width: "100%" }}>
          <Dropdown
            label="Status"
            placeholder="Status"
            options={workflowStatuses}
            multiple
            fullWidth
            onChange={(evt, val) => setFilterValue(val ? val.map(String) : [])}
            value={Array.isArray(filterValue) ? filterValue.map(String) : []}
            style={{ width: "100%" }}
          />
        </div>
      );
    } else if (
      (column.colId === "workflowType" || column.colId === "workflowName") &&
      !isWorkflowContext
    ) {
      return (
        <WorkflowNameInput
          label="Filter by Workflow Type"
          value={Array.isArray(filterValue) ? filterValue : []}
          onChange={(event, newValue) => {
            const valueToSet = Array.isArray(newValue) ? newValue : [];
            setFilterValue(valueToSet);
          }}
          placeholder="Select or type workflow type"
          fullWidth
        />
      );
    } else if (column.colId === "Key" || column.colId === "key") {
      return (
        <TextField
          label="Key value"
          value={filterValue}
          onChange={(e) => setFilterValue(e.target.value)}
          placeholder="Enter key value"
          fullWidth
        />
      );
    } else {
      return (
        <TextField
          label="Filter value"
          value={filterValue}
          onChange={(e) => setFilterValue(e.target.value)}
          placeholder="Enter filter value"
          fullWidth
        />
      );
    }
  };

  const open = Boolean(anchorEl);
  const id = open ? `filter-popover-${column.colId}` : undefined;

  const showFilterButton =
    isServerFilterable ||
    (isWorkflowContext && column.colId === "workflowName");

  return (
    <div className={`ag-header-cell-label ${classes.headerContainer}`}>
      <div className={classes.headerContent}>
        <div className={classes.headerTextContainer}>
          <div className={classes.headerText}>{displayName}</div>
          <div className={classes.iconsContainer}>
            {showFilterButton && (
              <IconButton
                size="small"
                onClick={handleFilterClick}
                className={
                  hasActiveFilter
                    ? `${classes.filterIcon} active`
                    : classes.filterIcon
                }
                title={hasActiveFilter ? "Filter applied" : "Add filter"}
              >
                <SearchIcon fontSize="small" />
              </IconButton>
            )}

            {isServerSortable && (
              <div
                onClick={handleSortClick}
                className={
                  isCurrentSortField
                    ? `${classes.sortButton} active`
                    : classes.sortButton
                }
                title={
                  isCurrentSortField
                    ? `Sorted ${
                        sortDirection === "DESC" ? "descending" : "ascending"
                      }. Click to ${
                        sortDirection === "DESC"
                          ? "sort ascending"
                          : "sort descending"
                      }`
                    : "Click to sort"
                }
              >
                {isCurrentSortField ? (
                  sortDirection === "DESC" ? (
                    <ArrowDownwardIcon fontSize="small" />
                  ) : (
                    <ArrowUpwardIcon fontSize="small" />
                  )
                ) : (
                  <CustomSortIcon
                    size={10}
                    style={{ opacity: 0.6, fill: "currentColor" }}
                  />
                )}
              </div>
            )}
          </div>
        </div>
        <Popover
          id={id}
          open={open}
          anchorEl={anchorEl}
          onClose={handleFilterClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "center",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "center",
          }}
        >
          <div className={classes.filterPopover}>
            {getFilterComponent()}

            {!isWorkflowContext ||
            (column.colId !== "workflowName" && column.colId !== "category") ? (
              <div className={classes.filterActions}>
                <Button
                  variant="outlined"
                  color="default"
                  onClick={handleFilterClear}
                >
                  Clear
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleFilterApply}
                >
                  Apply
                </Button>
              </div>
            ) : null}
          </div>
        </Popover>
      </div>
    </div>
  );
});

export default CustomHeaderComponent;
